import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { PlatformPicker, EMPTY_PLATFORMS, platformsToString } from '@/components/banners/PlatformPicker';
import { Send, MessageCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';

export const todayIST = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);
const EMPTY = { in_app_id: '', host_name: '', link: '', start_date: '' };

const PkgCard = ({ active, onClick, title, sub, price, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
        className={`text-left rounded-xl border-2 p-3 transition-colors ${active ? 'border-[#1F9D8A] bg-[#1F9D8A]/5' : 'border-border hover:bg-secondary/50'}`}>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
        <div className="font-display font-semibold mt-1">{price}</div>
    </button>
);

const BannerUpload = ({ file, setFile }) => {
    const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    return (
        <label className="relative flex aspect-[12/5] cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-border bg-secondary/40 hover:bg-secondary transition-colors" data-testid="promote-image-label">
            {preview ? <img src={preview} alt="banner preview" className="absolute inset-0 h-full w-full object-cover" />
                : <span className="flex flex-col items-center text-xs text-muted-foreground"><Upload className="h-6 w-6 mb-1" /> Banner image (required · ~1200×500, max 5 MB)</span>}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="promote-image-input" />
        </label>
    );
};

export const PromoRequestForm = ({ cfg, onSent }) => {
    const [pkg, setPkg] = useState(cfg.packages?.[0]?.id || 'custom');
    const [days, setDays] = useState(String(cfg.min_days));
    const [plats, setPlats] = useState(EMPTY_PLATFORMS);
    const [f, setF] = useState({ ...EMPTY, start_date: todayIST() });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const chosen = cfg.packages.find(p => p.id === pkg);
    const total = chosen ? chosen.price_inr : Number(cfg.price_per_day) * Number(days || 0);
    const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));

    const send = async () => {
        const d = Number(days);
        if (!chosen && (!d || d < cfg.min_days || d > cfg.max_days)) { toast.error(`Choose ${cfg.min_days}–${cfg.max_days} days`); return; }
        if (!file) { toast.error('Upload your banner image'); return; }
        if (!f.start_date) { toast.error('Pick the date you want the banner to go live'); return; }
        const fd = new FormData();
        fd.append('image', file);
        Object.entries(f).forEach(([k, v]) => fd.append(k, v));
        fd.append('platforms', platformsToString(plats));
        fd.append('package_id', chosen?.id || '');
        fd.append('days', String(chosen ? chosen.days : d));
        setBusy(true);
        try {
            await api.post('/promotions/requests', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Request sent! We will WhatsApp you once it is approved so you can pay.');
            setPlats(EMPTY_PLATFORMS); setF({ ...EMPTY, start_date: todayIST() }); setFile(null); onSent();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not send request'); }
        finally { setBusy(false); }
    };

    return (
        <Card className="p-4 sm:p-5" data-testid="promote-request-form">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Request a banner slot</div>
            <div className="mt-3 grid gap-2 grid-cols-2 sm:grid-cols-4">
                {cfg.packages.map(p => <PkgCard key={p.id} active={pkg === p.id} onClick={() => setPkg(p.id)} title={p.name} sub={`${p.days} days`} price={formatINR(p.price_inr)} testid={`promote-package-${p.id}`} />)}
                <PkgCard active={pkg === 'custom'} onClick={() => setPkg('custom')} title="Custom days" sub={`${formatINR(cfg.price_per_day)} / day`} price={`${cfg.min_days}–${cfg.max_days} days`} testid="promote-package-custom" />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><BannerUpload file={file} setFile={setFile} /></div>
                {!chosen && <div className="space-y-1.5"><Label className="text-xs">Number of days</Label><Input type="number" min={cfg.min_days} max={cfg.max_days} value={days} onChange={e => setDays(e.target.value)} data-testid="promote-days-input" /></div>}
                <div className="space-y-1.5"><Label className="text-xs">Go live from</Label><Input type="date" min={todayIST()} value={f.start_date} onChange={set('start_date')} data-testid="promote-start-date" /></div>
                <div className="sm:col-span-2"><PlatformPicker value={plats} onChange={setPlats} idPrefix="promote-platform" /></div>
                <div className="space-y-1.5"><Label className="text-xs">In-app ID (optional)</Label><Input value={f.in_app_id} onChange={set('in_app_id')} data-testid="promote-inapp-id" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Title / host name (optional)</Label><Input value={f.host_name} onChange={set('host_name')} data-testid="promote-host-name" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Profile link (optional)</Label><Input placeholder="https://…" value={f.link} onChange={set('link')} data-testid="promote-link" /></div>
            </div>
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3 rounded-xl bg-secondary/50 p-3">
                <div className="text-sm"><span className="text-muted-foreground">Total: </span><strong className="font-display text-lg" data-testid="promote-total">{formatINR(total || 0)}</strong>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1"><MessageCircle className="h-3 w-3" /> No payment now. After admin approval you get a WhatsApp message and a Pay now button here.</div></div>
                <Button onClick={send} disabled={busy} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="promote-send-request"><Send className="h-4 w-4 mr-1.5" />{busy ? 'Sending…' : 'Send request'}</Button>
            </div>
        </Card>
    );
};
